import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGenerationStore } from '../store/generationStore'

export function GenerationNotification() {
  const navigate = useNavigate()
  const {
    isGenerating,
    generationStep,
    currentGeneration,
    inputs,
    clearCurrentGeneration,
  } = useGenerationStore()

  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const totalImages = currentGeneration?.expectedImageCount ?? (inputs.fiveShotMode ? 5 : 1)
  const completedImages = currentGeneration?.images?.length || 0
  const isComplete = !isGenerating && currentGeneration && completedImages >= totalImages
  const isPartial = !isGenerating && currentGeneration && completedImages > 0 && completedImages < totalImages

  // Show notification when generation starts, completes, or partially completes
  useEffect(() => {
    if (isGenerating) {
      setDismissed(false)
      setIsVisible(true)
      setIsExiting(false)
    } else if ((isComplete || isPartial) && !dismissed) {
      setIsVisible(true)
      setIsExiting(false)
    }
  }, [isGenerating, currentGeneration, completedImages, dismissed])

  // Handle dismiss
  const handleDismiss = () => {
    setIsExiting(true)
    setTimeout(() => {
      setIsVisible(false)
      setIsExiting(false)
      setDismissed(true)
      if (isComplete || isPartial) {
        clearCurrentGeneration()
      }
    }, 300)
  }

  // Handle view in gallery
  const handleViewGallery = () => {
    handleDismiss()
    navigate('/gallery')
  }

  if (!isVisible || dismissed) return null

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 w-[340px] ${
        isExiting ? 'notification-exit-slide' : 'notification-enter-slide'
      }`}
    >
      <div className="glass-card rounded-2xl shadow-lg overflow-hidden">
        {/* Green accent bar at top */}
        <div className="h-1 bg-gradient-to-r from-primary to-primary-dark" />

        <div className="p-5">
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-surface-muted transition-colors text-text-muted hover:text-text-main"
            title="Dismiss"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>

          {/* Content */}
          <div className="flex flex-col items-center text-center">
            {/* Pulsing Orb */}
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isGenerating
                  ? 'bg-primary/10 animate-pulse-glow'
                  : 'bg-primary/20'
              }`}
            >
              <span
                className={`material-symbols-outlined text-3xl ${
                  isComplete ? 'text-primary filled' : isPartial ? 'text-amber-500 filled' : 'text-primary'
                }`}
              >
                {isComplete ? 'check_circle' : isPartial ? 'warning' : 'auto_awesome'}
              </span>
            </div>

            {/* Headline */}
            <h4 className="font-serif text-lg text-text-main mb-1">
              {isComplete
                ? 'Image ready'
                : isPartial
                  ? 'Partially complete'
                  : 'Creating your image...'}
            </h4>

            {/* Progress dots for 5-shot mode */}
            {totalImages > 1 && (
              <div className="flex items-center gap-2 my-3">
                {Array.from({ length: totalImages }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      i < completedImages
                        ? 'bg-primary scale-100'
                        : 'bg-gray-200 scale-90'
                    } ${i === completedImages - 1 && completedImages > 0 ? 'animate-dot-complete' : ''}`}
                  />
                ))}
              </div>
            )}

            {/* Status text */}
            <p className="text-sm text-text-muted mb-3">
              {isComplete ? (
                totalImages > 1
                  ? `All ${completedImages} images ready`
                  : 'Your image is ready to view'
              ) : isPartial ? (
                `${completedImages} of ${totalImages} images generated`
              ) : totalImages > 1 ? (
                completedImages > 0
                  ? `${completedImages} of ${totalImages} ready`
                  : generationStep || 'Starting generation...'
              ) : (
                generationStep || 'Starting generation...'
              )}
            </p>

            {/* Reassuring message during generation */}
            {isGenerating && (
              <p className="text-xs text-text-light">
                Feel free to keep browsing
              </p>
            )}

            {/* View in Gallery button on complete or partial */}
            {(isComplete || isPartial) && (
              <button
                onClick={handleViewGallery}
                className={`mt-3 w-full py-2.5 px-4 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                  isPartial
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                <span>
                  {isPartial
                    ? `View ${completedImages} Image${completedImages !== 1 ? 's' : ''} in Gallery`
                    : 'View in Gallery'}
                </span>
                <span className="material-symbols-outlined text-lg">arrow_forward</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
